import { useMemo, useState } from 'react';
import { residentStatus, useTrackedResidents } from '@/shared/hooks/useTracked';
import { useLocationHistory } from '@/shared/hooks/useLocationHistory';
import type { ResidentStatus } from '@/shared/firebase/types';
import { ResidentCard } from './ResidentCard';
import { MapPanel } from './MapPanel';
import { SosBanner } from './SosBanner';
import {
  ResidentDetailPanel,
  type PanelMode,
  type EditingGeofence,
  type HistoryRangeKey,
} from './ResidentDetailPanel';

type FilterKey = 'all' | ResidentStatus;

const RANGE_MS: Record<HistoryRangeKey, number> = {
  '1h':  1 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d':  7 * 24 * 60 * 60 * 1000,
};

export function CuidadorMain({ centerId }: { centerId: string }) {
  const { residents, loading } = useTrackedResidents(centerId);

  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [panelMode, setPanelMode] = useState<PanelMode>('info');
  const [editingGeofence, setEditingGeofence] = useState<EditingGeofence | null>(null);
  const [historyRange, setHistoryRange] = useState<HistoryRangeKey>('24h');

  const items = useMemo(
    () => residents.map((r) => ({ resident: r, status: residentStatus(r) })),
    [residents],
  );

  const counts = useMemo(() => {
    const c: Record<ResidentStatus, number> = { inside: 0, outside: 0, sos: 0, offline: 0 };
    items.forEach((i) => { c[i.status]++; });
    return c;
  }, [items]);

  const filtered = filter === 'all' ? items : items.filter((i) => i.status === filter);
  const sosResidents = items.filter((i) => i.status === 'sos').map((i) => i.resident);
  const selectedItem = selected ? items.find((i) => i.resident.uid === selected) : null;

  const { points: historyPoints } = useLocationHistory(
    centerId,
    panelMode === 'history' && selected ? selected : null,
    RANGE_MS[historyRange],
  );

  function handleSelect(uid: string) {
    if (!uid) { closePanel(); return; }
    if (selected !== uid) { setEditingGeofence(null); setPanelMode('info'); }
    setSelected(uid);
  }

  function closePanel() {
    setSelected(null);
    setEditingGeofence(null);
    setPanelMode('info');
  }

  return (
    <div className="h-full grid md:grid-cols-[380px_1fr] gap-3 min-h-0">
      <aside className="flex flex-col gap-3 min-h-0">
        {sosResidents.length > 0 && (
          <SosBanner residents={sosResidents} onJump={handleSelect} />
        )}

        <div className="grid grid-cols-4 gap-2">
          <Stat label="En zona"  value={counts.inside}  active={filter==='inside'}  onClick={()=>setFilter(filter==='inside'?'all':'inside')}   tone="success" />
          <Stat label="Fuera"    value={counts.outside} active={filter==='outside'} onClick={()=>setFilter(filter==='outside'?'all':'outside')} tone="warning" />
          <Stat label="SOS"      value={counts.sos}     active={filter==='sos'}     onClick={()=>setFilter(filter==='sos'?'all':'sos')}         tone="danger"  />
          <Stat label="S/señal"  value={counts.offline} active={filter==='offline'} onClick={()=>setFilter(filter==='offline'?'all':'offline')} tone="slate"   />
        </div>

        <div className="flex-1 overflow-y-auto pr-1 space-y-2 min-h-0">
          {loading ? (
            <p className="text-sm text-slate-500 text-center py-8">Cargando residentes…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">Sin residentes para este filtro.</p>
          ) : (
            filtered.map(({ resident, status }) => (
              <ResidentCard
                key={resident.uid}
                resident={resident}
                status={status}
                selected={selected === resident.uid}
                onClick={() => handleSelect(resident.uid)}
              />
            ))
          )}
        </div>
      </aside>

      <section className="relative min-h-[420px] md:min-h-0">
        <MapPanel
          items={items}
          selectedUid={selected}
          onSelect={handleSelect}
          editingGeofence={editingGeofence}
          onEditingGeofenceChange={setEditingGeofence}
          history={panelMode === 'history' ? historyPoints : []}
        />

        {selectedItem && (
          <ResidentDetailPanel
            centerId={centerId}
            resident={selectedItem.resident}
            status={selectedItem.status}
            mode={panelMode}
            setMode={(m) => {
              setPanelMode(m);
              if (m !== 'geofence') setEditingGeofence(null);
            }}
            editingGeofence={editingGeofence}
            setEditingGeofence={setEditingGeofence}
            historyRange={historyRange}
            setHistoryRange={setHistoryRange}
            historyCount={historyPoints.length}
            onClose={closePanel}
          />
        )}
      </section>
    </div>
  );
}

function Stat({
  label, value, active, onClick, tone,
}: { label: string; value: number; active: boolean; onClick: () => void; tone: 'success'|'warning'|'danger'|'slate' }) {
  const tones: Record<string, string> = {
    success: 'data-[active=true]:bg-success-50 data-[active=true]:text-success data-[active=true]:border-success/30',
    warning: 'data-[active=true]:bg-warning-50 data-[active=true]:text-warning data-[active=true]:border-warning/30',
    danger:  'data-[active=true]:bg-danger-50  data-[active=true]:text-danger  data-[active=true]:border-danger/30',
    slate:   'data-[active=true]:bg-slate-100  data-[active=true]:text-slate-700 data-[active=true]:border-slate-300',
  };
  return (
    <button
      data-active={active}
      onClick={onClick}
      className={`rounded-xl border border-slate-100 bg-white px-2 py-2 text-center transition hover:bg-slate-50 ${tones[tone]}`}
    >
      <p className="text-lg font-extrabold leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">{label}</p>
    </button>
  );
}
